import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useImperativeHandle, useRef, useState, } from 'react';
import styled from 'styled-components';
import { getEnhancedVideoProperties, getPreferredOrientationAdjustmentAngle, useScreenOrientation, } from '../utils';
import { useStreamrTvViewer } from '../utils/useStreamrTvViewer';
export function Canvas({ fit: fitProp = 'auto', streamPartId, controllerRef, onReady, onMuteChange, onVideoPropertiesChange: onVideoPropertiesChangeProp, followPreferredOrientation = false, ...props }) {
    const [canvas, setCanvas] = useState(null);
    const [videoProperties, setVideoProperties] = useState();
    const fit = fitProp === 'auto'
        ? videoProperties?.type === 'screen'
            ? 'contain'
            : 'cover'
        : fitProp;
    const screenOrientation = useScreenOrientation();
    useEffect(() => {
        function onResize() {
            const parent = canvas?.parentElement;
            if (!parent || videoProperties == null) {
                return;
            }
            const { width: intrinsicWidth, height: intrinsicHeight, rotatedWidth, rotatedHeight, rotationAngle: angle, } = followPreferredOrientation
                ? getEnhancedVideoProperties({
                    ...videoProperties,
                    videoOrientationAngle: videoProperties.videoOrientationAngle +
                        getPreferredOrientationAdjustmentAngle(videoProperties.preferredOrientation, screenOrientation),
                })
                : videoProperties;
            const scale = (fit === 'cover' ? Math.max : Math.min)(parent.clientWidth / rotatedWidth, parent.clientHeight / rotatedHeight);
            const width = intrinsicWidth * scale;
            const height = intrinsicHeight * scale;
            canvas.style.setProperty('--Canvas_Width', `${width}px`);
            canvas.style.setProperty('--Canvas_Height', `${height}px`);
            canvas.style.setProperty('--Canvas_OffsetX', `${parent.clientWidth / 2}px`);
            canvas.style.setProperty('--Canvas_OffsetY', `${parent.clientHeight / 2}px`);
            canvas.style.setProperty('--Canvas_Rotation', `${angle}deg`);
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [
        canvas,
        videoProperties,
        fit,
        screenOrientation,
        followPreferredOrientation,
    ]);
    const viewer = useStreamrTvViewer({
        streamPartId,
        canvas: canvas || undefined,
        onStarted: () => {
            onReady?.();
        },
    });
    const onMuteChangeRef = useRef(onMuteChange);
    if (onMuteChangeRef.current !== onMuteChange) {
        onMuteChangeRef.current = onMuteChange;
    }
    const onVideoPropertiesChangeRef = useRef(onVideoPropertiesChangeProp);
    if (onVideoPropertiesChangeRef.current !== onVideoPropertiesChangeProp) {
        onVideoPropertiesChangeRef.current = onVideoPropertiesChangeProp;
    }
    useEffect(() => {
        if (!viewer) {
            return;
        }
        let mounted = true;
        function onMuteChange() {
            if (mounted && viewer) {
                onMuteChangeRef.current?.(viewer.isAudioMuted());
            }
        }
        onMuteChange();
        viewer.on('muteChange', onMuteChange);
        function onVideoPropertiesChange(value) {
            const enhanced = getEnhancedVideoProperties(value);
            setVideoProperties(enhanced);
            onVideoPropertiesChangeRef.current?.(enhanced);
        }
        const videoProperties = viewer.getVideoProperties();
        if (videoProperties) {
            onVideoPropertiesChange(videoProperties);
        }
        viewer.on('videoPropertiesChanged', onVideoPropertiesChange);
        return () => {
            mounted = false;
            viewer.off('videoPropertiesChanged', onVideoPropertiesChange);
            viewer.off('muteChange', onMuteChange);
        };
    }, [viewer]);
    useImperativeHandle(controllerRef, () => ({
        mute: () => {
            viewer?.muteAudio();
        },
        unmute: () => {
            viewer?.unmuteAudio();
        },
    }), [viewer]);
    return _jsx(CanvasRoot, { ...props, ref: setCanvas });
}
const CanvasRoot = styled.canvas `
    width: var(--Canvas_Width, 100%);
    height: var(--Canvas_Height, 100%);
    transform: translate(-50%, -50%)
        translate(var(--Canvas_OffsetX, 0), var(--Canvas_OffsetY, 0))
        rotate(var(--Canvas_Rotation, 0deg));
    transform-origin: center;
`;
